import {
  type AdminAudit,
  type AdminCapture,
  type AdminEmails,
  type AdminEngines,
  type AdminJobs,
  type AdminModelUsage,
  type AdminOnboarding,
  type AdminOverview,
  type AdminRange,
  type AdminServices,
  type AdminSpend,
  type AdminStorage,
  type AuditQuery,
  type CustomerDetail,
  type CustomerList,
  type CustomerQuery,
  adminAuditSchema,
  adminCaptureSchema,
  adminEmailsSchema,
  adminEnginesSchema,
  adminJobsSchema,
  adminModelUsageSchema,
  adminOnboardingSchema,
  adminOverviewSchema,
  adminServicesSchema,
  adminSpendSchema,
  adminStorageSchema,
  customerDetailSchema,
  customerListSchema,
} from "@crux/contracts";
import { z } from "zod";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * The operator panel's live reads. Parsed with the same schemas the sample
 * data is, so a screen cannot tell which one it was handed — only the badge
 * can (`admin/data/source.ts`).
 */

export async function fetchAdminSpend(range: AdminRange): Promise<AdminSpend> {
  const { data, response } = await api.GET("/api/admin/spend", {
    params: { query: { days: range.days } },
  });
  if (data === undefined) throw new Error(problem(response.status, "load the spend"));
  return adminSpendSchema.parse(data);
}

export async function fetchAdminModelUsage(range: AdminRange): Promise<AdminModelUsage> {
  const { data, response } = await api.GET("/api/admin/model-usage", {
    params: { query: { days: range.days } },
  });
  if (data === undefined) throw new Error(problem(response.status, "load model usage"));
  return adminModelUsageSchema.parse(data);
}

/** Rust-only read, like /api/admin/me; parsed against the shared wire schema. */
export async function fetchAdminCapture(range: AdminRange): Promise<AdminCapture> {
  const response = await fetch(`/api/admin/capture?days=${String(range.days)}`, {
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error(problem(response.status, "load capture health"));
  return adminCaptureSchema.parse(await response.json());
}

/** Rust-only reads, like /api/admin/me; parsed against the shared wire schemas. */
export async function fetchAdminCustomers(query: CustomerQuery): Promise<CustomerList> {
  const params = new URLSearchParams({
    limit: String(query.limit),
    offset: String(query.offset),
    search: query.search,
    filter: query.filter,
    sort: query.sort,
    direction: query.direction,
  });
  const response = await fetch(`/api/admin/customers?${params.toString()}`, {
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error(problem(response.status, "load the customers"));
  return customerListSchema.parse(await response.json());
}

export async function fetchAdminCustomer(workspaceId: string): Promise<CustomerDetail> {
  const response = await fetch(
    `/api/admin/customers/${encodeURIComponent(workspaceId)}`,
    {
      credentials: "same-origin",
    },
  );
  if (!response.ok) throw new Error(problem(response.status, "load the customer"));
  return customerDetailSchema.parse(await response.json());
}

export async function fetchAdminOnboarding(): Promise<AdminOnboarding> {
  const response = await fetch("/api/admin/onboarding", { credentials: "same-origin" });
  if (!response.ok) throw new Error(problem(response.status, "load onboarding"));
  return adminOnboardingSchema.parse(await response.json());
}

export async function fetchAdminEmails(range: AdminRange): Promise<AdminEmails> {
  const response = await fetch(`/api/admin/emails?days=${String(range.days)}`, {
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error(problem(response.status, "load the emails"));
  return adminEmailsSchema.parse(await response.json());
}

export async function fetchAdminStorage(): Promise<AdminStorage> {
  const response = await fetch("/api/admin/storage", { credentials: "same-origin" });
  if (!response.ok) throw new Error(problem(response.status, "load storage"));
  return adminStorageSchema.parse(await response.json());
}

export async function fetchAdminJobs(): Promise<AdminJobs> {
  const response = await fetch("/api/admin/jobs", { credentials: "same-origin" });
  if (!response.ok) throw new Error(problem(response.status, "load the jobs"));
  return adminJobsSchema.parse(await response.json());
}

export async function fetchAdminEngines(): Promise<AdminEngines> {
  const response = await fetch("/api/admin/engines", { credentials: "same-origin" });
  if (!response.ok) throw new Error(problem(response.status, "load the engines"));
  return adminEnginesSchema.parse(await response.json());
}

export async function fetchAdminServices(): Promise<AdminServices> {
  const response = await fetch("/api/admin/services", { credentials: "same-origin" });
  if (!response.ok) throw new Error(problem(response.status, "load the services"));
  return adminServicesSchema.parse(await response.json());
}

export async function fetchAdminOverview(): Promise<AdminOverview> {
  const response = await fetch("/api/admin/overview", { credentials: "same-origin" });
  if (!response.ok) throw new Error(problem(response.status, "load the overview"));
  return adminOverviewSchema.parse(await response.json());
}

export async function fetchAdminAudit(query: AuditQuery): Promise<AdminAudit> {
  const params = new URLSearchParams({
    limit: String(query.limit),
    offset: String(query.offset),
  });
  const response = await fetch(`/api/admin/audit?${params.toString()}`, {
    credentials: "same-origin",
  });
  if (!response.ok) throw new Error(problem(response.status, "load the audit log"));
  return adminAuditSchema.parse(await response.json());
}

const staffSchema = z.object({ staff: z.boolean() });

/**
 * Whether the signed-in person may use the operator panel. Rust only (like
 * People, a plain request parsed with a schema); asked before the panel draws.
 */
export async function fetchIsStaff(): Promise<boolean> {
  const response = await fetch("/api/admin/me", { credentials: "same-origin" });
  if (!response.ok) throw new Error(problem(response.status, "check your access"));
  return staffSchema.parse(await response.json()).staff;
}
